import apiClient, { OperationResult } from "../../services/apiClient";
import { trackEvent } from "../analytics/analytics";
import {
  Store,
  StoreDb,
  StoreItem,
  StorePage,
  StorePageRank,
  StoreRef,
} from "./store";

export const getStoreDb = async (): Promise<StoreDb> => {
  const response = await apiClient.get<Store[]>("/stores/list");
  const stores = response.data.map(
    (store) =>
      ({
        store: { ...store, name: store.name ?? "Main Showroom" },
      } as StoreRef)
  );
  return { stores, version: 1 };
};

export const saveStore = async (
  model: Store
): Promise<OperationResult<Store>> => {
  trackEvent({
    category: "showroom",
    action: model.id > 0 ? "update" : "create",
    label: model.id + (model.isPrimary ? "[P]" : "") + ": " + model.name,
    feature: true,
  });
  const response = await apiClient.post<OperationResult<Store>>(
    "/stores",
    model
  );
  return response.data;
};

export const deleteStore = async (
  id: number
): Promise<OperationResult<string>> => {
  trackEvent({
    category: "showroom",
    action: "delete",
    label: id.toString(),
  });
  const response = await apiClient.delete<OperationResult<string>>(
    `/stores/${id}`
  );
  return response.data;
};

export const getStoreProducts = async (
  storeId: number
): Promise<StoreItem[]> => {
  const response = await apiClient.get<StoreItem[]>(
    `/stores/${storeId}/products`
  );
  return response.data;
};

export const updateStoreProducts = async (
  storeId: number,
  items: StoreItem[]
): Promise<OperationResult<Store>> => {
  trackEvent({
    category: "showroom",
    action: "update-products",
    label: storeId.toString(),
    value: items.length,
  });
  const response = await apiClient.post<OperationResult<Store>>(
    `/stores/${storeId}/products`,
    items
  );
  return response.data;
};

export const initStorePage = (page: StorePage): StorePage => ({
  ...page,
  content: JSON.parse(page.jsonContent ?? "{}"),
});

export const getStorePages = async (storeId: number): Promise<StorePage[]> => {
  const response = await apiClient.get<{ pages: StorePage[] }>(
    `/stores/${storeId}/pages`
  );
  const pages = response.data.pages.map(initStorePage);
  return pages;
};

// Save a store page
export const saveStorePage = async (page: StorePage): Promise<StorePage> => {
  trackEvent({
    category: "showroom",
    action: page.pageId > 0 ? "update-page" : "create-page",
    label: page.title,
    feature: true,
  });
  const response = await apiClient.post<{ page: StorePage }>(
    `/stores/page`,
    page
  );
  return response.data.page;
};

// Delete a store page
export const deleteStorePage = async (pageId: number): Promise<boolean> => {
  trackEvent({
    category: "showroom",
    action: "delete-page",
    label: pageId.toString(),
  });
  const response = await apiClient.delete<{ success: boolean }>(
    `/stores/page/${pageId}`
  );
  return response.data.success;
};

// Update page ranks/order
export const saveStorePageRanks = async (
  storeId: number,
  ranks: StorePageRank[]
): Promise<boolean> => {
  trackEvent({
    category: "showroom",
    action: "reorder-pages",
    value: ranks.length,
  });
  const response = await apiClient.post<{ success: boolean }>(
    `/stores/${storeId}/page-ranks`,
    ranks
  );
  return response.data.success;
};
